import {
    GeographicalChartPoint,
    GeographicalCountrySummary,
    GeographicalProjectLink,
    GeographicalSector,
    GeographicalSummaryResponse
} from './geographical-summary.types';
import {
    GeographicalChartPointDto,
    GeographicalProjectLinkDto,
    LegacyChartDto,
    LegacyGeographicalSummaryDto,
    GeographicalSummaryCountryDto,
    GeographicalSummaryDto,
    GeographicalSummarySectorDto
} from './geographical-summary.api-contracts';

function asNumber(value: unknown, fallback = 0): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
}

function asString(value: unknown, fallback = ''): string {
    return typeof value === 'string' ? value : fallback;
}

export function mapSector(dto: GeographicalSummarySectorDto): GeographicalSector {
    const sectorDto = dto as GeographicalSummarySectorDto & {
        Id?: number;
        SectorId?: number;
        Name?: string;
        SectorName?: string;
    };

    return {
        id: asNumber(sectorDto.id ?? sectorDto.sectorId ?? sectorDto.Id ?? sectorDto.SectorId),
        name: asString(sectorDto.name ?? sectorDto.sectorName ?? sectorDto.Name ?? sectorDto.SectorName)
    };
}

export function mapCountry(dto: GeographicalSummaryCountryDto): GeographicalCountrySummary {
    const countryDto = dto as GeographicalSummaryCountryDto & {
        EicRegionId?: number;
        RegionId?: number;
        EicCountryId?: number;
        CountryId?: number;
        Sid?: string;
        CountryName?: string;
        Name?: string;
        IsAccessible?: boolean;
        ProjectCount?: number;
        Count?: number;
        Capex?: number;
        Value?: number;
        ColorHex?: string;
        Fill?: string;
    };

    return {
        eicRegionId: asNumber(countryDto.eicRegionId ?? countryDto.regionId ?? countryDto.EicRegionId ?? countryDto.RegionId),
        eicCountryId: asNumber(countryDto.eicCountryId ?? countryDto.countryId ?? countryDto.EicCountryId ?? countryDto.CountryId),
        sid: countryDto.sid ?? countryDto.Sid,
        countryName: asString(countryDto.countryName ?? countryDto.name ?? countryDto.CountryName ?? countryDto.Name),
        isAccessible: (countryDto.isAccessible ?? countryDto.IsAccessible) !== false,
        projectCount: asNumber(countryDto.projectCount ?? countryDto.count ?? countryDto.ProjectCount ?? countryDto.Count),
        capex: asNumber(countryDto.capex ?? countryDto.value ?? countryDto.Capex ?? countryDto.Value),
        colorHex: asString(countryDto.colorHex ?? countryDto.fill ?? countryDto.ColorHex ?? countryDto.Fill, '') || undefined
    };
}

export function mapSummary(dto: GeographicalSummaryDto | null | undefined): GeographicalSummaryResponse {
    const summaryDto = dto as GeographicalSummaryDto & {
        Sectors?: GeographicalSummarySectorDto[];
        Countries?: GeographicalSummaryCountryDto[];
    };

    return {
        sectors: (summaryDto?.sectors ?? summaryDto?.Sectors ?? []).map(mapSector),
        countries: (summaryDto?.countries ?? summaryDto?.Countries ?? []).map(mapCountry)
    };
}

export function mapChartPoint(dto: GeographicalChartPointDto): GeographicalChartPoint {
    const stageKey = asString(dto.stageKey ?? dto.stage);
    const stageLabel = asString(dto.stageLabel ?? dto.label, stageKey);

    return {
        startupYear: asNumber(dto.startupYear ?? dto.year),
        stageKey,
        stageLabel,
        value: asNumber(dto.value)
    };
}

export function mapProjectLink(dto: GeographicalProjectLinkDto): GeographicalProjectLink {
    const projectStage = asString(dto.projectStage ?? dto.stage);
    return {
        projectId: asNumber(dto.projectId ?? dto.id),
        projectName: asString(dto.projectName ?? dto.name),
        projectType: asString(dto.projectType ?? dto.type),
        startupYear: asNumber(dto.startupYear ?? dto.year ?? dto.startUp),
        projectStage: normaliseStageKey(projectStage),
        value: asNumber(dto.value)
    };
}

function hslToHex(h: number, s: number, l: number): string {
    const hue = h / 360;
    const sat = s / 100;
    const lig = l / 100;

    let r: number;
    let g: number;
    let b: number;

    if (sat === 0) {
        r = g = b = lig;
    } else {
        const hue2rgb = (p: number, q: number, t: number): number => {
            let x = t;
            if (x < 0) x += 1;
            if (x > 1) x -= 1;
            if (x < 1 / 6) return p + (q - p) * 6 * x;
            if (x < 1 / 2) return q;
            if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
            return p;
        };

        const q = lig < 0.5 ? lig * (1 + sat) : lig + sat - lig * sat;
        const p = 2 * lig - q;
        r = hue2rgb(p, q, hue + 1 / 3);
        g = hue2rgb(p, q, hue);
        b = hue2rgb(p, q, hue - 1 / 3);
    }

    const toHex = (x: number): string => {
        const hex = Math.round(x * 255).toString(16);
        return hex.length === 1 ? `0${hex}` : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function legacyColor(logCapex: number, maxLogCapex: number, hue: number): string | undefined {
    if (!logCapex || !maxLogCapex) {
        return undefined;
    }

    const scale = logCapex / maxLogCapex;
    return hslToHex(hue, 50, (1.0 - scale) * 50 + 50);
}

export function mapLegacySummary(dto: LegacyGeographicalSummaryDto | null | undefined): GeographicalSummaryResponse {
    const countrySummaries = dto?.countrySummaries || [];
    const maxLogCapex = Math.max(
        ...countrySummaries.map((country) => asNumber(country.sectorSummary?.logCapex, 0)),
        0
    );

    return {
        sectors: [],
        countries: countrySummaries.map((country) => {
            const sectorSummary = country.sectorSummary || {};
            const count = asNumber(sectorSummary.count ?? country.count, 0);
            const logCapex = asNumber(sectorSummary.logCapex, 0);
            const hue = asNumber(sectorSummary.hue, 0);

            return {
                eicRegionId: asNumber(country.eicRegionId ?? country.regionId, 0),
                eicCountryId: asNumber(country.eicCountryId ?? country.countryId, 0),
                sid: asString(country.sid),
                countryName: asString(country.countryName ?? country.name),
                isAccessible: country.isAccessible !== false,
                projectCount: count,
                capex: asNumber(sectorSummary.capex, 0),
                colorHex: legacyColor(logCapex, maxLogCapex, hue)
            };
        })
    };
}

function normaliseStageKey(stage: string): string {
    return stage.replace(/\s|-/g, '');
}

export function mapLegacyChart(dto: LegacyChartDto | null | undefined): GeographicalChartPoint[] {
    const startupYears = dto?.startupYears || [];
    const points: GeographicalChartPoint[] = [];

    startupYears.forEach((yearItem) => {
        const startupYear = asNumber(yearItem.startupYear, 0);
        (yearItem.projectStages || []).forEach((stageItem) => {
            const stageLabel = asString(stageItem.projectStage);
            points.push({
                startupYear,
                stageKey: normaliseStageKey(stageLabel),
                stageLabel,
                value: asNumber(stageItem.value)
            });
        });
    });

    return points;
}
